import React, { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';

const AdminApplicationStats = ({ selectedSchool, applicationStatuses, isSuperAdmin }) => {
  const [stats, setStats] = useState({
    totalApplications: 0,
    responseRate: 0,
    offerRate: 0,
    averageTimeToResponse: 0,
    statusDistribution: {},
    topCompanies: [],
    last7Days: {
      totalApplications: 0,
      responses: 0,
      offers: 0,
      activeUsers: 0,
    },
  });

  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showDetails, setShowDetails] = useState(false);
  const [chartData, setChartData] = useState({
    series: [],
    options: {
      chart: {
        type: 'line',
        height: 350,
        toolbar: {
          show: false,
        },
      },
      stroke: {
        curve: 'smooth',
        width: 2,
      },
      xaxis: {
        type: 'datetime',
        labels: {
          style: {
            colors: '#6B7280',
          },
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: '#6B7280',
          },
        },
      },
      tooltip: {
        theme: 'light',
        x: {
          format: 'MMM dd, yyyy',
        },
      },
      colors: ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444'],
      grid: {
        borderColor: '#E5E7EB',
        strokeDashArray: 4,
      },
    },
  });

  // Calculate statistics from application statuses
  const calculateStats = (statuses) => {
    if (!statuses.length) return;

    // Group statuses by application_id and get the most recent status for each
    const latestStatuses = statuses.reduce((acc, status) => {
      if (
        !acc[status.application_id] ||
        new Date(status.status_date) > new Date(acc[status.application_id].status_date)
      ) {
        acc[status.application_id] = status;
      }
      return acc;
    }, {});

    const stats = {
      totalApplications: Object.keys(latestStatuses).length,
      responseRate: 0,
      offerRate: 0,
      averageTimeToResponse: 0,
      statusDistribution: {},
      topCompanies: [],
      last7Days: {
        totalApplications: 0,
        responses: 0,
        offers: 0,
        activeUsers: 0,
      },
    };

    // Calculate last 7 days metrics
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    // Track unique users who have been active in the last 7 days
    const activeUsers = new Set();

    Object.values(latestStatuses).forEach((status) => {
      const statusDate = new Date(status.status_date);

      // Count total applications in last 7 days
      if (statusDate >= sevenDaysAgo) {
        stats.last7Days.totalApplications++;
        activeUsers.add(status.user_id); // Track unique users

        // Count responses in last 7 days
        if (
          ['Live Interviewing', 'Offer Received', 'Rejected', 'Online Assessment'].includes(
            status.status
          )
        ) {
          stats.last7Days.responses++;
        }

        // Count offers in last 7 days
        if (status.status === 'Offer Received') {
          stats.last7Days.offers++;
        }
      }
    });

    stats.last7Days.activeUsers = activeUsers.size;

    // Calculate status distribution using only the latest statuses
    Object.values(latestStatuses).forEach((status) => {
      stats.statusDistribution[status.status] = (stats.statusDistribution[status.status] || 0) + 1;
    });

    // Calculate response rate (applications that got past "Application Submitted")
    const respondedApplications = Object.values(latestStatuses).filter((s) =>
      ['Live Interviewing', 'Offer Received', 'Rejected', 'Online Assessment'].includes(s.status)
    ).length;
    stats.responseRate = (respondedApplications / stats.totalApplications) * 100;

    // Calculate offer rate
    const offeredApplications = Object.values(latestStatuses).filter(
      (s) => s.status === 'Offer Received'
    ).length;
    stats.offerRate = (offeredApplications / stats.totalApplications) * 100;

    setStats(stats);
  };

  // Prepare chart data
  const prepareChartData = (statuses) => {
    if (!statuses.length) return;

    // Group statuses by date and status
    const statusGroups = statuses.reduce((acc, status) => {
      const date = new Date(status.status_date).toISOString().split('T')[0];
      if (!acc[date]) {
        acc[date] = {};
      }
      acc[date][status.status] = (acc[date][status.status] || 0) + 1;
      return acc;
    }, {});

    // Get all unique statuses
    const allStatuses = [...new Set(statuses.map((s) => s.status))];

    // Create series data
    const series = allStatuses.map((status) => ({
      name: status,
      data: Object.entries(statusGroups).map(([date, counts]) => ({
        x: new Date(date).getTime(),
        y: counts[status] || 0,
      })),
    }));

    // Filter series based on selected status
    const filteredSeries =
      selectedStatus === 'all' ? series : series.filter((s) => s.name === selectedStatus);

    setChartData((prev) => ({
      ...prev,
      series: filteredSeries,
    }));
  };

  useEffect(() => {
    calculateStats(applicationStatuses);
    prepareChartData(applicationStatuses);
  }, [applicationStatuses, selectedStatus]);

  if (!selectedSchool) return null;

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Application Analytics</h2>
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 flex items-center"
        >
          {showDetails ? 'Hide Details' : 'Show Details'}
          <svg
            className={`w-5 h-5 ml-2 transform transition-transform duration-200 ${showDetails ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Stats Grid Section */}
      <div
        className={`${showDetails ? 'bg-gray-50' : ''} rounded-lg ${showDetails ? 'p-6' : 'p-2'} mb-6`}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Applications Card */}
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-blue-100 text-blue-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Applications</p>
                <div className="flex items-baseline">
                  <p className="text-xl font-semibold text-gray-700">{stats.totalApplications}</p>
                  <p className="ml-2 text-xs text-gray-500">
                    (+{stats.last7Days.totalApplications} last 7d)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Response Rate Card */}
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-green-100 text-green-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Response Rate</p>
                <div className="flex items-baseline">
                  <p className="text-xl font-semibold text-gray-700">
                    {stats.responseRate.toFixed(1)}%
                  </p>
                  <p className="ml-2 text-xs text-gray-500">
                    (+{stats.last7Days.responses} last 7d)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Offer Rate Card */}
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-purple-100 text-purple-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Offer Rate</p>
                <div className="flex items-baseline">
                  <p className="text-xl font-semibold text-gray-700">
                    {stats.offerRate.toFixed(1)}%
                  </p>
                  <p className="ml-2 text-xs text-gray-500">(+{stats.last7Days.offers} last 7d)</p>
                </div>
              </div>
            </div>
          </div>

          {/* Active Users Card */}
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-indigo-100 text-indigo-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Active Users</p>
                <div className="flex items-baseline">
                  <p className="text-xl font-semibold text-gray-700">
                    {stats.last7Days.activeUsers}
                  </p>
                  <p className="ml-2 text-xs text-gray-500">(last 7d)</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Sections */}
      {showDetails && (
        <>
          {/* Timeline Chart Section */}
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-700">Application Timeline</h3>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="p-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-gray-700 bg-white"
              >
                <option value="all">All applications</option>
                {Object.keys(stats.statusDistribution).map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <ReactApexChart
                options={chartData.options}
                series={chartData.series}
                type="line"
                height={350}
              />
            </div>
          </div>

          {/* Application Statuses Table Section - Only show for super admins */}
          {isSuperAdmin && (
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Application Statuses</h3>
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Company
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {applicationStatuses.length > 0 ? (
                      applicationStatuses
                        .sort((a, b) => new Date(b.status_date) - new Date(a.status_date))
                        .map((status) => (
                          <tr key={status.status_id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {status.user.first_name} {status.user.last_name}
                              <br />
                              <span className="text-gray-500">{status.user.email}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {status.company_name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {status.status}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {new Date(status.status_date).toLocaleString()}
                            </td>
                          </tr>
                        ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                          No application statuses found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminApplicationStats;
